import type {
  ClientExtras,
  ClientNamespaces,
  ClientResponse,
} from '@minusjs/tuf/client';
import type { ComputedRef } from 'vue';
import {
  asAxios,
  AxiosAdapter,
  createClient,
} from '@minusjs/tuf/client';
import { descreteApi } from '@shared/naive-ui';

declare module '@minusjs/tuf/client' {
  interface ClientRequestOptions {
    notification?: boolean;
    /** Set on the replay after a 401 */
    retried?: boolean;
  }
}

export const sharedClient = createClient<ClientNamespaces<any> & ClientExtras>(
  {
    endpoint: '/api/tuf',
  },
  new AxiosAdapter(),
);

sharedClient.adapter.onResponse(undefined, (requestError) => {
  if (requestError.request.options?.notification) {
    descreteApi.notification.error({
      title: 'Error',

      content: (requestError.response?.data as any)?.message || requestError.message,
      duration: 3000,
    });
  }

  return Promise.reject(requestError);
});

export function useSharedClient<T>() {
  return sharedClient as T & ClientExtras;
}

export function thenData<T extends ClientResponse>(value: T): Promise<T['data']> {
  return Promise.resolve(value.data);
}

export const axiosClient = sharedClient.use(asAxios);
export function useAxios() {
  return axiosClient;
}

let sessionNoticeAt = 0;

// The refresh token has a limited life: past it, a request stays refused and nothing explains why.
// Offline there is no 401 (the request fails before), so this only speaks when the server answered
function notifySessionExpired() {
  if (Date.now() - sessionNoticeAt < 60_000)
    return;
  sessionNoticeAt = Date.now();
  descreteApi.notification.error({
    title: 'Session expired',
    content: 'Sign in again to keep working online. What you recorded is kept on this device.',
    duration: 8000,
  });
}

export function setupAuthOnClient(auth: {
  token: ComputedRef<string | undefined>;
  fetchToken(): Promise<string>;
}) {
  const { token, fetchToken } = auth;

  sharedClient.adapter.onRequest((request) => {
    if (!request.options)
      request.options = { headers: {} };
    if (!request.options.headers)
      request.options.headers = {};
    // No token yet: the 401 below fetches one, an invalid `Bearer undefined` adds nothing
    if (token.value)
      request.options.headers.authorization = `Bearer ${token.value}`;
    return request;
  });

  sharedClient.adapter.onResponse(undefined, async (error) => {
    // A request is replayed once: a second 401 is a real one, not a loop to start again
    if (error.response?.status === 401 && !error.request.options?.retried) {
      try {
        const token = await fetchToken();
        if (token) {
          error.request.options = { ...error.request.options, retried: true };
          return sharedClient.adapter.execute(error.request);
        }
      }
      catch {
        // Reported below
      }
      notifySessionExpired();
    }
    throw error;
  });

  axiosClient.interceptors.request.use(async (config) => {
    if (token.value)
      config.headers.Authorization = `Bearer ${token.value}`;
    return config;
  });

  axiosClient.interceptors.response.use(undefined, async (error: any) => {
    if (error.response?.status === 401 && !error.config?.retried) {
      if (!token.value)
        await fetchToken().catch(() => undefined);
      if (token.value) {
        // Same request with the new token: its other headers (content type…) stay
        error.config.headers.set('Authorization', `Bearer ${token.value}`);
        return axiosClient.request({ ...error.config, retried: true });
      }
      notifySessionExpired();
    }
    throw error;
  });
}
